import React, { useEffect, useMemo, useState } from "react";
import { getTransform } from "../../../../../utils/getTransform";
import { TOOL_PREP_PREVIEW_BG } from "../../../../../utils/editorEntrySession";
import {
  computeFittedStageSize,
  TOOL_PREP_STAGE_INSET,
  useReportSubjectPlacement,
} from "../../../lib/toolPrepImageLayout";
import { useToolPrepSubjectObject } from "../../../hooks/useToolPrepSubjectObject";
import RoundLoader from "../../../../../components/ui/RoundLoader";
import ToolPrepSubjectBoundingBox from "../../../shared/Popup/ToolPrepSubjectBoundingBox";
import {
  ToolPrepSubjectCropGhost,
  subjectMainImageStyleForCrop,
  subjectWrapperStyleForCrop,
} from "../../../shared/Popup/ToolPrepSubjectCropGhost";
import SoftWatermarkOverlay from "../../../shared/Popup/SoftWatermarkOverlay";
import ToolPrepCanvasStage from "../../../shared/Popup/ToolPrepCanvasStage";
import "../../../shared/Popup/ToolPrepStage.css";
import "./TransformPreview.css";

const TransformPreview = ({
  imageUrl = null,
  rotation = 0,
  flipH = false,
  flipV = false,
  loading = false,
  previewBg = TOOL_PREP_PREVIEW_BG.TRANSPARENT,
  stageBackgroundColor = null,
  emptyLabel = "Upload an image to preview",
  showSubjectBox = false,
  softWatermarkToolKey = "rotate",
  showSoftWatermark = true,
}) => {
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [stageSize, setStageSize] = useState({ width: 420, height: 420 });
  const stageRef = React.useRef(null);

  const canvasAspect = useMemo(() => {
    if (naturalSize.width > 0 && naturalSize.height > 0) {
      return naturalSize.width / naturalSize.height;
    }
    return 1;
  }, [naturalSize]);

  useEffect(() => {
    if (!imageUrl) {
      setNaturalSize({ width: 0, height: 0 });
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    if (
      typeof imageUrl === "string" &&
      /^https?:\/\//i.test(imageUrl) &&
      !imageUrl.startsWith("blob:") &&
      !imageUrl.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => {
      if (cancelled) return;
      setNaturalSize({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      });
    };
    img.onerror = () => {
      if (!cancelled) setNaturalSize({ width: 0, height: 0 });
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [imageUrl]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      const fitted = computeFittedStageSize(
        el.clientWidth,
        el.clientHeight,
        canvasAspect,
        { inset: TOOL_PREP_STAGE_INSET }
      );
      if (fitted) setStageSize(fitted);
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [canvasAspect, imageUrl]);

  const transform = useMemo(() => {
    if (!naturalSize.width || !naturalSize.height || !stageSize.width) {
      return { width: 0, height: 0, x: 0, y: 0 };
    }
    return getTransform(
      naturalSize.width,
      naturalSize.height,
      stageSize.width,
      stageSize.height,
      "fit"
    );
  }, [naturalSize, stageSize]);

  const fitFrame = useMemo(() => {
    if (!(transform.width > 0)) return null;
    return {
      x: transform.x,
      y: transform.y,
      width: transform.width,
      height: transform.height,
    };
  }, [transform.x, transform.y, transform.width, transform.height]);

  const interactive = showSubjectBox && Boolean(imageUrl) && !loading;
  const [isCropping, setIsCropping] = useState(false);
  const {
    object,
    setObject,
    wrapperStyle,
    imageStyle,
    contentCrop,
    crop,
    cropExtent,
    originalImageSize,
  } = useToolPrepSubjectObject({
    enabled: interactive,
    imageUrl,
    fitFrame,
    resetKey: imageUrl,
  });

  useReportSubjectPlacement(
    null,
    object,
    contentCrop,
    stageSize,
    imageUrl
  );

  const cssTransform = [
    `rotate(${Number(rotation) || 0}deg)`,
    `scaleX(${flipH ? -1 : 1})`,
    `scaleY(${flipV ? -1 : 1})`,
  ].join(" ");

  let canvasBg = "#1a1a1a";
  if (previewBg === TOOL_PREP_PREVIEW_BG.WHITE) {
    canvasBg = "#FFFFFF";
  } else if (previewBg === TOOL_PREP_PREVIEW_BG.COLOR && stageBackgroundColor) {
    canvasBg = stageBackgroundColor;
  } else if (previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT) {
    canvasBg = "transparent";
  }

  const checker =
    previewBg === TOOL_PREP_PREVIEW_BG.TRANSPARENT ||
    !imageUrl ||
    (previewBg === TOOL_PREP_PREVIEW_BG.ORIGINAL && !imageUrl);

  const fitStyle = fitFrame
    ? {
        left: fitFrame.x,
        top: fitFrame.y,
        width: fitFrame.width,
        height: fitFrame.height,
        transform: cssTransform,
      }
    : { transform: cssTransform };

  return (
    <ToolPrepCanvasStage
      className="transform-preview"
      ref={stageRef}
      metaImageUrl={imageUrl}
      metaWidth={naturalSize.width}
      metaHeight={naturalSize.height}
    >
      <div
        className={`transform-preview__canvas${
          checker ? " transform-preview__canvas--checker" : ""
        }`}
        style={{
          width: stageSize.width,
          height: stageSize.height,
          backgroundColor: checker ? undefined : canvasBg,
        }}
      >
        <div className="tool-prep-stage__clip">
          {loading ? (
            <div className="transform-preview__loading">
              <RoundLoader size="md" label="Removing background…" />
            </div>
          ) : imageUrl && interactive && object && wrapperStyle ? (
            <div
              className="transform-preview__subject"
              style={subjectWrapperStyleForCrop(
                {
                  ...wrapperStyle,
                  transform: cssTransform,
                  transformOrigin: "center center",
                },
                isCropping
              )}
            >
              <ToolPrepSubjectCropGhost
                active={isCropping}
                src={imageUrl}
                style={imageStyle}
              />
              <img
                src={imageUrl}
                alt="Transform preview"
                className="transform-preview__image"
                style={subjectMainImageStyleForCrop(imageStyle, {
                  isCropping,
                  crop,
                  originalImageSize,
                  object,
                })}
                draggable={false}
              />
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt="Transform preview"
              className={
                fitFrame
                  ? "transform-preview__image"
                  : "transform-preview__image transform-preview__image--contain"
              }
              style={fitStyle}
              draggable={false}
            />
          ) : (
            <div className="transform-preview__empty">{emptyLabel}</div>
          )}
          {!loading && imageUrl ? (
            <SoftWatermarkOverlay
              visible={showSoftWatermark}
              toolKey={softWatermarkToolKey}
            />
          ) : null}
        </div>
        {imageUrl && interactive && object && wrapperStyle ? (
          <div className="tool-prep-stage__chrome">
            <ToolPrepSubjectBoundingBox
              enabled
              interactive
              frame={object}
              crop={crop}
              cropExtent={cropExtent}
              originalImageSize={originalImageSize}
              onChange={setObject}
              onCroppingChange={setIsCropping}
              stageSize={stageSize}
              rotation={Number(rotation) || 0}
              flipX={flipH ? -1 : 1}
              flipY={flipV ? -1 : 1}
              showRotate={false}
            />
          </div>
        ) : null}
      </div>
    </ToolPrepCanvasStage>
  );
};

export default TransformPreview;
